import { isEqual } from 'shared/comparers';

import { booleanProblem as ohioBooleanProblem, singleProblem as ohioSingleProblem } from './ohioUtils';
import { booleanProblem as illinoisBooleanProblem } from './illinoisUtils';
import { booleanProblem as floridaBooleanProblem } from './floridaUtils';

import {
    OHIO,
    FLORIDA,
    ILLINOIS,
    PENNSYLVANIA,
    NORTH_CAROLINA,
    HIGH_SCHOOL,
    MINIMUM_WAGE_BY_STATE,
    DEGREE,
    ECE_DEGREE,
    OH_SINGLE_QUALIFICATION_PROBLEM_CHOICE_IDS,
    OH_BOOLEAN_QUALIFICATION_PROBLEM_NAMES,
    IL_BOOLEAN_QUALIFICATION_PROBLEM_CHOICE_IDS,
    FL_BOOLEAN_QUALIFICATION_PROBLEM_CHOICE_IDS,
    PA_BOOLEAN_QUALIFICATION_PROBLEM_NAMES,
    GLOBAL_BOOLEAN_QUALIFICATION_PROBLEM_NAMES,
    BASE_EDUCATION_OPTION_IDS,
} from './constants';

import type {
    GenericChoice,
    GenericQualificationChoices,
    IManagedScale,
    IPayScaleQualification,
    Problems,
} from './types';

function rateProblem(scale: IManagedScale, state: string): 'toolow' | 'bad' | null {
    if (isNaN(scale.rate)) {
        return 'bad';
    }
    if (scale.rate < MINIMUM_WAGE_BY_STATE[state as keyof typeof MINIMUM_WAGE_BY_STATE]) {
        return 'toolow';
    }

    return null;
}

export function baseEducationProblem(scale: IManagedScale): boolean {
    const education = scale.qualifications.find((x: IPayScaleQualification) => [DEGREE, ECE_DEGREE].includes(x.name));
    return !BASE_EDUCATION_OPTION_IDS.includes(education?.option_id ?? null) && education?.option !== HIGH_SCHOOL;
}

export function checkForDuplicates(scales: IManagedScale[]): { [key: string]: boolean } {
    const qualificationsMap: { [key: string]: object } = {};

    scales.forEach((x: IManagedScale) => {
        const qualificationMap = {} as { [key: string]: number | string | null };
        x.qualifications.map((y: IPayScaleQualification) => {
            qualificationMap[`${y.qualification_id}`] = y.option_id ?? y.value;
        });
        qualificationsMap[`${x.id}`] = qualificationMap;
    });

    const duplicateMap: { [key: string]: boolean } = {};

    scales.forEach((x: IManagedScale, index: number) => {
        const qualificationMap = qualificationsMap[`${x.id}`];

        scales.slice(index + 1).forEach((y: IManagedScale) => {
            const otherQualificationMap = qualificationsMap[`${y.id}`];

            if (isEqual(qualificationMap, otherQualificationMap)) {
                duplicateMap[`${x.id}`] = true;
                duplicateMap[`${y.id}`] = true;
            }
        });
    });

    return duplicateMap;
}

function resolveBooleanIdsByName(
    qualificationChoices: GenericQualificationChoices | undefined,
    names: string[],
): number[] {
    if (!qualificationChoices) return [];
    return qualificationChoices.boolean
        .filter((b: GenericChoice) => names.includes(b.qualification?.name ?? ''))
        .map((b: GenericChoice) => b.key);
}

const CPL_QUALIFICATION_ID = 9;

function isCplLevelOne(scale: IManagedScale, qualificationChoices: GenericQualificationChoices | undefined): boolean {
    if (!qualificationChoices) return false;
    const cplQual = scale.qualifications.find(
        (q: IPayScaleQualification) => q.qualification_id === CPL_QUALIFICATION_ID,
    );
    if (!cplQual?.option_id) return false;
    const cplChoices = qualificationChoices.single.find((s) => s.qualification.id === CPL_QUALIFICATION_ID);
    const selectedOption = cplChoices?.choices.find((c: GenericChoice) => c.key === cplQual.option_id);
    const optionValue = selectedOption?.value ?? '';
    return !optionValue.includes('II') && !optionValue.includes('V');
}

export function findProblems(
    scale: IManagedScale,
    state: string,
    initialScale: boolean = false,
    initialScaleId: string = '',
    qualificationChoices?: GenericQualificationChoices,
): Problems {
    const problems = {
        education: false,
        experience: false,
        single: [],
        multi: [],
        boolean: {},
        rate: null,
        noQualifications: false,
        duplicate: false,
    } as Problems;

    problems.education = initialScale ? baseEducationProblem(scale) : false;
    problems.experience = false;
    problems.noQualifications = !initialScale && scale.id !== initialScaleId && scale.qualifications.length < 1;

    const booleans = scale.qualifications.filter(
        (x: IPayScaleQualification) => !x.option_id && x.value?.toString() === 'true',
    );

    const globalProblemIds = resolveBooleanIdsByName(qualificationChoices, GLOBAL_BOOLEAN_QUALIFICATION_PROBLEM_NAMES);

    switch (state) {
        case OHIO:
            problems.single = initialScale
                ? ohioSingleProblem(scale.qualifications, OH_SINGLE_QUALIFICATION_PROBLEM_CHOICE_IDS)
                : [];
            if (initialScale && isCplLevelOne(scale, qualificationChoices)) {
                delete (problems.single as { [key: string]: boolean })[`${CPL_QUALIFICATION_ID}`];
            }
            problems.multi = {};
            problems.boolean = initialScale
                ? ohioBooleanProblem(booleans, [
                      ...globalProblemIds,
                      ...resolveBooleanIdsByName(qualificationChoices, OH_BOOLEAN_QUALIFICATION_PROBLEM_NAMES),
                  ])
                : {};
            problems.rate = rateProblem(scale, state);
            break;
        case FLORIDA:
            problems.single = {};
            problems.multi = {};
            problems.boolean = initialScale
                ? floridaBooleanProblem(booleans, [...globalProblemIds, ...FL_BOOLEAN_QUALIFICATION_PROBLEM_CHOICE_IDS])
                : {};
            problems.rate = rateProblem(scale, state);
            break;
        case ILLINOIS:
            problems.single = {};
            problems.multi = {};
            problems.boolean = initialScale
                ? illinoisBooleanProblem(booleans, [
                      ...globalProblemIds,
                      ...IL_BOOLEAN_QUALIFICATION_PROBLEM_CHOICE_IDS,
                  ])
                : {};
            problems.rate = rateProblem(scale, state);
            break;
        case PENNSYLVANIA:
            problems.single = {};
            problems.multi = {};
            problems.boolean = initialScale
                ? illinoisBooleanProblem(booleans, [
                      ...globalProblemIds,
                      ...resolveBooleanIdsByName(qualificationChoices, PA_BOOLEAN_QUALIFICATION_PROBLEM_NAMES),
                  ])
                : {};
            problems.rate = rateProblem(scale, state);
            break;
        case NORTH_CAROLINA:
            problems.single = {};
            problems.multi = {};
            problems.boolean = initialScale ? illinoisBooleanProblem(booleans, globalProblemIds) : {};
            problems.rate = rateProblem(scale, state);
            break;
        default:
            problems.single = {};
            problems.multi = {};
            problems.boolean = {};
            problems.rate = rateProblem(scale, state);
            break;
    }

    return problems;
}

export function updateProblems(
    scale: IManagedScale,
    problems: { [key: string]: Problems },
    setProblems: (problems: { [key: string]: Problems }) => void,
    state: string,
): void {
    const newProblems = findProblems(scale, state);

    setProblems({ ...problems, [scale.id]: newProblems });
}
